import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Box, Button, Typography, Paper, Divider, Chip } from '@mui/material'
import { ArrowLeft, CheckCircle2, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { resetConsolidation } from '../../../redux/actions/boConsolidationAction'
import BOListTable from './bo/BOListTable'
import BOCleaningProgress from './bo/BOCleaningProgress'
import BOReviewPanel from './bo/BOReviewPanel'
import BOExecutionProgress from './bo/BOExecutionProgress'
import BOConsolidationPreviewPanel from './bo/BOConsolidationPreviewPanel'

export default function BOConsolidationTab() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { phase, selectedBo, executionProgress } = useSelector((s) => s.boConsolidation)

  const handleBackToList = () => {
    dispatch(resetConsolidation())
  }

  return (
    <Box>
      {/* Back button + BO info header for non-select phases */}
      {phase !== 'select' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button
            startIcon={<ArrowLeft size={16} />}
            onClick={handleBackToList}
            sx={{ textTransform: 'none', color: '#c9a96e' }}
          >
            {t('du_bo_back_to_list', 'Back to BO List')}
          </Button>
          {selectedBo && (
            <>
              <Box sx={{ width: 1, height: 24, borderLeft: '1px solid', borderColor: 'divider' }} />
              <FileText size={18} color="#c9a96e" />
              <Typography variant="subtitle1" sx={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 600 }}>
                {selectedBo.bo_num}
              </Typography>
              <Chip
                size="small"
                label={(selectedBo.language || 'fr').toUpperCase()}
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  bgcolor: selectedBo.language === 'ar' ? '#3a4d3f' : '#3a3f4d',
                  color: '#ffffff',
                  letterSpacing: 1,
                }}
              />
              {selectedBo.bo_date && (
                <Typography variant="body2" color="text.secondary">
                  {new Date(selectedBo.bo_date).toLocaleDateString()}
                </Typography>
              )}
            </>
          )}
        </Box>
      )}

      {/* Phase: Select */}
      {phase === 'select' && <BOListTable />}

      {/* Phase: Cleaning */}
      {phase === 'cleaning' && <BOCleaningProgress />}

      {/* Phase: Review */}
      {phase === 'review' && <BOReviewPanel />}

      {/* Phase: Consolidation preview */}
      {phase === 'consolidation-preview' && <BOConsolidationPreviewPanel />}

      {/* Phase: Executing */}
      {phase === 'executing' && <BOExecutionProgress />}

      {/* Phase: Done */}
      {phase === 'done' && (
        <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <CheckCircle2 size={64} color="#4caf50" style={{ marginBottom: 16 }} />
          <Typography
            variant="h5"
            sx={{ mb: 2, fontFamily: '"Cormorant Garamond", serif', fontWeight: 600 }}
          >
            {t('du_bo_consolidation_complete', 'Consolidation Complete')}
          </Typography>

          {selectedBo && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {selectedBo.bo_num}
            </Typography>
          )}

          {executionProgress.result && (
            <Box sx={{ mt: 2, mb: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>
                    {executionProgress.result.success || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('du_bo_succeeded', 'Succeeded')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: executionProgress.result.failed > 0 ? '#f44336' : 'text.secondary' }}>
                    {executionProgress.result.failed || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('du_bo_failed', 'Failed')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          <Button
            variant="contained"
            startIcon={<ArrowLeft size={16} />}
            onClick={handleBackToList}
            sx={{
              mt: 2,
              textTransform: 'none',
              backgroundColor: '#c9a96e',
              '&:hover': { backgroundColor: '#b8944f' },
            }}
          >
            {t('du_bo_back_to_list', 'Back to BO List')}
          </Button>
        </Paper>
      )}
    </Box>
  )
}
